import { useMemo } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { ProjectChangedEvent, ProjectDTO, ProjectsSnapshot } from '@pace/contracts';
import { viewOf } from '@pace/contracts';
import type { ProjectView } from '@pace/domain';
import { api } from '../lib/api';
import { syncServerTime } from '../lib/time';
import { useLiveStatus } from '../live/stream';
import { useOutbox } from '../offline/hooks';
import { applyPending } from '../offline/optimistic';
import type { Lookups } from './bootstrap';

/** Stable empty list so memoised selectors don't recompute while loading. */
export const NO_PROJECTS: ProjectDTO[] = [];

export const projectsKey = (all = false) => ['projects', all ? 'all' : 'live'] as const;

/**
 * Every project the user may see (live ones, or all with `all`), kept live by SSE and
 * overlaid with this device's unsynced taps. Falls back to polling if the stream is down.
 */
export function useProjects(all = false) {
  const live = useLiveStatus();
  const q = useQuery({
    queryKey: projectsKey(all),
    queryFn: async () => {
      const sent = Date.now();
      const s = await api<ProjectsSnapshot>(`/projects${all ? '?all=1' : ''}`);
      syncServerTime(s.serverTime, sent);
      return s.projects;
    },
    staleTime: 30_000,
    refetchInterval: live === 'open' ? 5 * 60_000 : 20_000,
    networkMode: 'offlineFirst',
  });
  const queue = useOutbox();
  const data = useMemo(() => (q.data ? applyPending(q.data, queue) : undefined), [q.data, queue]);
  return { ...q, data };
}

/** One project (from the live list when there, else fetched on its own). */
export function useProject(id: string | undefined) {
  const list = useProjects();
  const inList = list.data?.find((p) => p.id === id);
  const one = useQuery({
    queryKey: ['project', id],
    queryFn: () => api<ProjectDTO>(`/projects/${id}`),
    enabled: !!id && !!list.data && !inList,
    networkMode: 'offlineFirst',
  });
  const queue = useOutbox();
  const fetched = useMemo(
    () => (one.data ? applyPending([one.data], queue)[0] : undefined),
    [one.data, queue],
  );
  return {
    data: inList ?? fetched,
    isPending: list.isPending || (!inList && one.isPending && !!list.data),
    error: one.error,
  };
}

/** Forecasts for a list of projects, recomputed when the data or the day changes. */
export function useViews(projects: ProjectDTO[] | undefined, lk: Lookups | null): Map<string, ProjectView> {
  const today = lk?.today();
  return useMemo(() => {
    const m = new Map<string, ProjectView>();
    if (!projects || !lk || !today) return m;
    for (const p of projects) m.set(p.id, viewOf(p, today, lk.cal));
    return m;
  }, [projects, lk, today]);
}

/** Merge a live event into every cached copy of that project. */
export function applyProjectEvent(qc: QueryClient, e: ProjectChangedEvent): void {
  for (const [key, list] of qc.getQueriesData<ProjectDTO[]>({ queryKey: ['projects'] })) {
    if (!list) continue;
    const i = list.findIndex((p) => p.id === e.projectId);
    if (!e.project) {
      if (i >= 0)
        qc.setQueryData<ProjectDTO[]>(
          key,
          list.filter((p) => p.id !== e.projectId),
        );
    } else if (i < 0) {
      qc.setQueryData<ProjectDTO[]>(key, [...list, e.project]);
    } else if (e.project.version >= list[i]!.version) {
      qc.setQueryData<ProjectDTO[]>(
        key,
        list.map((p) => (p.id === e.projectId ? e.project! : p)),
      );
    }
  }
  if (e.project) qc.setQueryData(['project', e.projectId], e.project);
  else qc.removeQueries({ queryKey: ['project', e.projectId] });
}
